import { tinyButtons } from '@byecode/ui'
import type {
    ActorEvent,
    BaseFieldConfig,
    ButtonItem,
    GalleryViewOptions,
    HighLightConditions,
    IndependentAction,
    ObjectFitConfig,
    Pagination,
    RatioConfig,
    RecordLikeProtocol,
    RichTextContentProtocol,
    ViewField,
    ViewOptions
} from '@lighthouse/core'
import { useUpdateEffect } from '@react-hookz/web'
import { useVirtualizer } from '@tanstack/react-virtual'
import type { ReactNode } from 'react'
import React, { useLayoutEffect, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { useLatest } from 'react-use'
import styled from 'styled-components'

import { useViewBlockContext } from '../../context'
import { GallerySpacing } from './constants'
import { GalleryRecord } from './GalleryRecord'

const Root = styled.div`
    overflow: auto hidden;
    padding-bottom: 4px;
    ${tinyButtons}
`

const List = styled.div`
    position: relative;
`

const ItemWrapper = styled.div`
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
`

interface HorizontalScrollViewProps {
    id?: string
    scale?: number
    cols: number
    canPaginate?: boolean
    pagination: Pagination
    headCover?: Omit<BaseFieldConfig, 'align'> & ObjectFitConfig & RatioConfig
    columns: ViewField[]
    rowStyleConditions: Record<string, HighLightConditions>
    headAvatar: GalleryViewOptions['headAvatar']
    headTitle?: BaseFieldConfig
    headTags: GalleryViewOptions['headTags']
    contentTags: GalleryViewOptions['contentTags']
    footTags: GalleryViewOptions['footTags']
    recordStyle: GalleryViewOptions['style']
    recordOperator?: ButtonItem[]
    eventList?: ActorEvent[]
    renderItem?: (record: RecordLikeProtocol) => ReactNode
    onRecordOperatorActionTrigger?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
    onRecordClick?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
    onRenderButtonTitle: (v: RichTextContentProtocol, record?: RecordLikeProtocol) => string
    onLoadMoreData?: (pageNum: number) => Promise<RecordLikeProtocol[]>
}

export const HorizontalScrollView = (props: HorizontalScrollViewProps) => {
    const {
        id,
        scale = 1,
        cols,
        canPaginate,
        pagination,
        headCover,
        columns,
        recordStyle,
        rowStyleConditions,
        renderItem,
        contentTags,
        onRenderButtonTitle,
        onRecordClick,
        onRecordOperatorActionTrigger,
        headAvatar,
        headTags,
        headTitle,
        footTags,
        onLoadMoreData
    } = props
    const { currentPage = 1, pageSize = 10, rowTotal = 0 } = pagination
    const overscan = Math.ceil(pageSize / 5)

    const { recordPoints } = useViewBlockContext()
    const innerRef = useRef<HTMLDivElement | null>(null)

    const [containerWidth, setContainerWidth] = useState(innerRef.current?.clientWidth ?? 0)
    const itemWidth = (containerWidth - (Math.ceil(cols) - 1) * GallerySpacing) * (1 / cols)
    useLayoutEffect(() => {
        const el = innerRef.current
        if (!el) {
            return
        }

        let raf: number
        const observer = new ResizeObserver(([e]) => {
            raf = requestAnimationFrame(() => {
                flushSync(() => {
                    setContainerWidth(e.contentRect.width)
                })
            })
        })

        observer.observe(el)

        return () => {
            if (raf) {
                cancelAnimationFrame(raf)
            }

            if (observer) {
                observer.disconnect()
            }
        }
    }, [])

    const virtualizer = useVirtualizer({
        horizontal: true,
        count: recordPoints.length,
        getItemKey: i => recordPoints[i].id,
        getScrollElement: () => innerRef.current,
        estimateSize: () => containerWidth * (1 / cols),
        overscan
        // gap: GallerySpacing
    })
    const items = virtualizer.getVirtualItems()

    // 滚动加载
    const loadingRef = useRef(false)
    const latest = useLatest({ canPaginate })

    const inRange = currentPage < Math.ceil(rowTotal / pageSize)
    const isReachingTheRight = virtualizer.range ? virtualizer.range.endIndex >= recordPoints.length - 1 - overscan : false
    const hasMore = rowTotal > recordPoints.length
    const canLoadMore = inRange && isReachingTheRight && hasMore

    useUpdateEffect(() => {
        void (async () => {
            if (loadingRef.current || !latest.current.canPaginate) {
                return
            }

            if (canLoadMore && onLoadMoreData) {
                loadingRef.current = true
                await onLoadMoreData(currentPage + 1)

                loadingRef.current = false
            }
        })()
    }, [currentPage, canLoadMore, onLoadMoreData])

    // 收集子项高度
    const itemHeightRef = useRef<Map<string | number | bigint, HTMLElement | null>>(new Map())
    const [currentMaxHeight, setCurrentMaxHeight] = useState<number>()
    useLayoutEffect(() => {
        let maxHeight = 0
        let raf: number

        function handleResize() {
            items.forEach(item => {
                const el = itemHeightRef.current.get(item.key)
                if (!el) {
                    return
                }
                const oldMinHeight = el.style.minHeight
                el.style.minHeight = ''
                const { height } = el.getBoundingClientRect()
                el.style.minHeight = oldMinHeight
                maxHeight = Math.max(maxHeight, height)
            })

            if (maxHeight !== currentMaxHeight) {
                flushSync(() => {
                    setCurrentMaxHeight(maxHeight)
                })
            }
        }

        const observer = new ResizeObserver(e => {
            raf = requestAnimationFrame(handleResize)
        })

        items.forEach(item => {
            const el = itemHeightRef.current.get(item.key)
            if (!el) {
                return
            }
            observer.observe(el)
        })

        return () => {
            if (raf) {
                cancelAnimationFrame(raf)
            }

            if (observer) {
                observer.disconnect()
            }
        }
    }, [currentMaxHeight, items])

    return (
        <Root ref={innerRef} id={id}>
            <List
                style={{
                    height: currentMaxHeight && currentMaxHeight / scale,
                    width: virtualizer.getTotalSize() / scale
                }}
            >
                {items.map(item => (
                    <ItemWrapper
                        data-view-item={item.index}
                        key={String(item.key)}
                        style={{
                            width: itemWidth / scale,
                            transform: `translateX(${item.start / scale}px)`
                        }}
                    >
                        <GalleryRecord
                            ref={r => itemHeightRef.current.set(item.key, r)}
                            style={{ minHeight: '100%' }}
                            id={recordPoints[item.index].id}
                            headCover={headCover}
                            columns={columns}
                            rowStyleConditions={rowStyleConditions}
                            headAvatar={headAvatar}
                            headTitle={headTitle}
                            headTags={headTags}
                            contentTags={contentTags}
                            footTags={footTags}
                            recordStyle={recordStyle}
                            renderItem={renderItem}
                            onRecordOperatorActionTrigger={onRecordOperatorActionTrigger}
                            onRecordClick={onRecordClick}
                            onRenderButtonTitle={onRenderButtonTitle}
                        />
                    </ItemWrapper>
                ))}
            </List>
        </Root>
    )
}
